/**
 * PF Revamp — auto-save subscription.
 *
 * Mounts once at the PF root (`PurchaseFlowRevamp.page.tsx`) and wires:
 *   - A Zustand `subscribe` over the persisted slice fingerprint → fires on
 *     any mutation of property / cart / configs / instructions / billing.
 *   - ~800ms debounce so tight edits coalesce.
 *   - Auto-creates a draftId on the first save if one doesn't exist yet.
 *   - Skips while `draft.isHydrating` is true so reloads don't round-trip.
 *   - Idempotent: stringified payload is compared to the last successful
 *     save and a no-op skip short-circuits the mutation.
 *
 * State machine mirrored on `draftSlice`:
 *   (change) → (debounce) → markSaving → save() → markSaved
 *                                              └─→ markError
 *
 * Route cache invalidation happens via the `useSaveDraft` / `useCreateDraft`
 * mutation hooks (see `purchaseFlowDrafts.query.ts`) — this hook just
 * kicks off `mutateAsync`.
 *
 * Exported for testability:
 *   - `AUTOSAVE_DEBOUNCE_MS` — timing constant.
 *   - `selectPersistedFingerprint` — selector used for subscribe diffing.
 */

import { useEffect, useRef } from 'react'
import { shallow } from 'zustand/shallow'

import { logger } from 'utils/logging'

import { useCreateDraft, useSaveDraft } from 'dataQueries/purchaseFlowDrafts.query'
import { serializeDraft } from './serializeDraft'
import { usePfStore } from './store'
import type { PfStoreState } from './store'
import {
  dispatchPfRevampEvent,
  makeDraftSavedGate,
  PfRevampEventName
} from '../analytics'

/** Debounce window. Tuned to 800ms per master plan §4. */
export const AUTOSAVE_DEBOUNCE_MS = 800

/**
 * Pulls the persisted-slice primitives the subscription should react to.
 * Returns a plain object so `subscribeWithSelector` can shallow-compare on
 * each mutation — reference identity stays stable while non-persisted slices
 * (catalogue UI, stepNav) churn.
 */
export const selectPersistedFingerprint = (state: PfStoreState) => ({
  property: state.property,
  items: state.items,
  productConfigs: state.productConfigs,
  // W-30: serializeDraft also emits targetUserId / targetWorkspaceId (as
  // workspaceId) / cartItemUserConfirmed (as configsUserConfirmed). Without
  // watching them here, a mutation touching ONLY one of the three (e.g.
  // markConfigSaved flipping a confirmed flag with no config write in the
  // same window, or setAdminTarget as the last mutation before the tab is
  // abandoned) never arms the debounce and is lost on reload.
  targetUserId: state.targetUserId,
  targetWorkspaceId: state.targetWorkspaceId,
  targetUserEmail: state.targetUserEmail,
  adminSkipCtAutomation: state.adminSkipCtAutomation,
  adminSkipExternalProviderSending: state.adminSkipExternalProviderSending,
  // Nested record, but productConfigSlice replaces the object on every real
  // write (setConfig/markConfigSaved/markConfigDirty/clearConfig/
  // dropOrphanedStagingRooms all spread into a fresh reference and no-op out
  // on true no-change), so the shallow compare below sees a new reference
  // whenever the flag actually flips — same treatment as `productConfigs`.
  cartItemUserConfirmed: state.cartItemUserConfirmed,
  meeting: state.meeting,
  ctBookingId: state.ctBookingId,
  ctBookingName: state.ctBookingName,
  extraServices: state.extraServices,
  comments: state.comments,
  scheduledByCT: state.scheduledByCT,
  scheduleMethod: state.scheduleMethod,
  invoicingAddress: state.invoicingAddress,
  paymentMethod: state.paymentMethod,
  externalReporting: state.externalReporting,
  reference: state.reference,
  propertyOwner: state.propertyOwner,
  ccEmails: state.ccEmails,
  // The uploads a line owns, and the session they live on — both are serialized
  // now, and both can change with NOTHING else changing (deleting a file from a
  // committed line touches no item). Same W-30 reasoning as above: unwatched
  // means the debounce is never armed and the change is lost on reload.
  attachments: state.attachments,
  uploadDisplayNames: state.uploadDisplayNames,
  sessionId: state.draft.sessionId,
  sessionWorkspaceId: state.draft.sessionWorkspaceId,
  // Saved so a resume reopens the step the client left, even when they only navigated.
  currentStep: state.currentStep,
  // draftId included so once it flips to a real id we re-fingerprint and
  // avoid false "same state" on the next save.
  draftId: state.draft.draftId,
})

/** Single-mount hook. Attach from `PurchaseFlowRevamp.page.tsx`. */
export function usePfAutoSave(): void {
  const saveDraft = useSaveDraft()
  const createDraft = useCreateDraft()

  // Stable refs around the mutations so the subscription callback doesn't
  // need to resubscribe when React Query hands us a fresh mutate function.
  const saveRef = useRef(saveDraft.mutateAsync)
  const createRef = useRef(createDraft.mutateAsync)
  saveRef.current = saveDraft.mutateAsync
  createRef.current = createDraft.mutateAsync

  // Last successfully-persisted payload (stringified) + debounce timer.
  const lastSavedPayloadRef = useRef<string | null>(null)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Gates the `pf_revamp.draft_saved` event to at most 1 per 30 seconds per
  // session — auto-save fires frequently, analytics doesn't need each tick.
  const draftSavedAnalyticsGateRef = useRef(makeDraftSavedGate())

  useEffect(() => {
    // Unmount flag so the async save chain does not commit store
    // mutations (setDraftId, markSaved, markError) after the revamp root
    // unmounts. Without this guard, a save in-flight during navigation would
    // call markSaved() on the already-cleaned-up store, and the stale
    // `lastSavedPayloadRef` on the new mount starts at null — causing an
    // immediate redundant save on re-entry.
    let unmounted = false

    const runSave = async () => {
      const state = usePfStore.getState()
      if (state.draft.isHydrating || state.draft.isClosed) return

      const dto = serializeDraft(state)
      const serialized = JSON.stringify(dto)
      if (serialized === lastSavedPayloadRef.current) {
        // No-op: state matches what's already on disk.
        state.markSaved()
        return
      }

      try {
        state.markSaving()

        let draftId = state.draft.draftId
        if (!draftId) {
          draftId = await createRef.current()
          // Bail out if we navigated away while createDraft was in-flight.
          if (unmounted) return
          usePfStore.getState().setDraftId(draftId)
        }

        // Re-read state post-create so the DTO carries the freshly-minted id.
        const finalDto = serializeDraft(usePfStore.getState())
        await saveRef.current({ draftId, dto: finalDto })

        // Bail out if we navigated away while save was in-flight.
        if (unmounted) return

        lastSavedPayloadRef.current = JSON.stringify(finalDto)
        usePfStore.getState().markSaved()

        // Analytics — gated to 1/30s so rapid-edit sessions don't spam.
        const savedState = usePfStore.getState()
        const draftSavedEvent = {
          type: PfRevampEventName.DRAFT_SAVED,
          payload: {
            draftId: savedState.draft.draftId ?? '',
            itemCount: savedState.items.length,
          },
        } as const
        if (draftSavedAnalyticsGateRef.current(draftSavedEvent)) {
          dispatchPfRevampEvent(draftSavedEvent)
        }
      }
      catch (err) {
        if (unmounted) return
        logger.error('Auto-save failed', err, {
          context: { component: 'usePfAutoSave', action: 'save' },
        })
        usePfStore.getState().markError(
          err instanceof Error ? err.message : 'Auto-save failed'
        )
      }
    }

    const unsubscribe = usePfStore.subscribe(
      selectPersistedFingerprint,
      () => {
        const { draft } = usePfStore.getState()
        // Guard: hydration replay AND in-flight save (the save itself assigns
        // draftId, which re-triggers this subscription).
        if (draft.isHydrating) return
        if (draft.isClosed) return
        if (draft.saveStatus === 'saving') return

        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current)
        }
        debounceTimerRef.current = setTimeout(() => {
          debounceTimerRef.current = null
          void runSave()
        }, AUTOSAVE_DEBOUNCE_MS)
      },
      // Replace hand-rolled shallowEqual with zustand/shallow (handles
      // undefined keys and edge cases the local implementation missed).
      { equalityFn: shallow }
    )

    return () => {
      unmounted = true
      unsubscribe()
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }
    }
  }, [])
}
